import { CbhIcon, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { type ReactNode } from "react";

import { Button } from "./Button";

type IconType = "map-pin" | "clock" | "clock-filled" | "break";

type ActivityIcon =
  | {
      type: "icon";
      iconType: IconType;
    }
  | {
      type: "image";
      src: string;
      alt?: string;
    };

export interface ActivityItem {
  id: string;
  icon: ActivityIcon;
  title: string;
  subtitle?: ReactNode | ReactNode[];
  timestamp: string;
  onEdit?: () => void;
}

interface ActivityListProps {
  activities: ActivityItem[];
}

function toSubtitleLines(subtitle: ActivityItem["subtitle"]): ReactNode[] {
  if (Array.isArray(subtitle)) {
    return subtitle;
  }

  return isDefined(subtitle) ? [subtitle] : [];
}

export function ActivityList(props: ActivityListProps) {
  const { activities } = props;

  return (
    <Stack gap={7}>
      {activities.map((activity, index) => (
        <Stack
          key={activity.id}
          direction="row"
          gap={3}
          alignItems="center"
          width="100%"
          sx={{ position: "relative", minHeight: 48 }}
        >
          {index < activities.length - 1 && (
            <Box
              sx={{
                position: "absolute",
                left: 15,
                top: "calc(50% + 15px)",
                bottom: (theme) => `calc(-${theme.spacing(7)} - 50% + 15px)`,
                width: "1px",
                borderLeft: (theme) => `1px dashed ${theme.palette.grey[500]}`,
                pointerEvents: "none",
                opacity: 0.8,
              }}
            />
          )}
          <Box
            data-testid={`activity-icon-${
              activity.icon.type === "icon"
                ? `${activity.icon.iconType}-${activity.id}`
                : activity.id
            }`}
            sx={{
              alignSelf: "center",
              width: 30,
              height: 30,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              backgroundColor: "transparent",
              position: "relative",
              zIndex: 1,
              overflow: "hidden",
            }}
          >
            {activity.icon.type === "icon" ? (
              <CbhIcon size="small" type={activity.icon.iconType} />
            ) : (
              <Box
                component="img"
                src={activity.icon.src}
                alt={activity.icon.alt ?? activity.title}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            )}
          </Box>

          <Stack gap={1} sx={{ flex: 1, minWidth: 0 }}>
            <Text bold variant="body2">
              {activity.title}
            </Text>
            {toSubtitleLines(activity.subtitle).map((line, index) => (
              <Text
                // oxlint-disable-next-line react/no-array-index-key -- subtitles are positionally stable
                key={index}
                variant="caption"
                data-testid={`activity-${activity.id}-subtitle-${index}`}
                sx={{ color: (theme) => theme.palette.grey[700] }}
              >
                {line}
              </Text>
            ))}
          </Stack>

          {isDefined(activity.timestamp) &&
            (isDefined(activity.onEdit) ? (
              <Button
                aria-label={`Edit ${activity.title}`}
                endIconType="pencil"
                variant="contained"
                sx={(theme) => ({
                  flexShrink: 0,
                  fontSize: theme.typography.body2.fontSize,
                  letterSpacing: theme.typography.body2.letterSpacing,
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  boxShadow: theme.shadows[3],
                  "&:hover": {
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: theme.shadows[3],
                  },
                })}
                onClick={activity.onEdit}
              >
                {activity.timestamp}
              </Button>
            ) : (
              <Text semibold variant="body2" sx={{ flexShrink: 0 }}>
                {activity.timestamp}
              </Text>
            ))}
        </Stack>
      ))}
    </Stack>
  );
}
